import React from "react";
import "bootstrap-icons/font/bootstrap-icons.css";

const iconStyle = {
  fontSize: '1.2rem',
  verticalAlign: 'middle',
  display: 'inline-block'
};

const parseMarkdownTable = (tableLines) => {
  if (tableLines.length < 2) return null;

  const headers = tableLines[0]
    .split("|")
    .map((c) => c.trim())
    .filter(Boolean);

  const rows = tableLines.slice(2).map((row) =>
    row
      .split("|")
      .map((c) => c.trim())
      .filter(Boolean)
  );

  return (
    <div className="overflow-x-auto my-4">
      <table className="min-w-full border border-gray-300 rounded-lg">
        <thead className="bg-gray-100">
          <tr>
            {headers.map((header, idx) => (
              <th
                key={idx}
                className="border border-gray-300 px-4 py-2 text-left font-semibold"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-gray-50">
              {row.map((cell, cellIdx) => (
                <td
                  key={cellIdx}
                  className="border border-gray-300 px-4 py-2"
                  dangerouslySetInnerHTML={{
                    __html: formatTextInline(cell),
                  }}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const formatTextInline = (text) => {
  let formatted = text;
  // Bold **text**
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Italic *text*
  formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
  return formatted;
};

const formatLines = (text) => {
  const lines = text.split("\n");
  const elements = [];

  let listBuffer = [];
  let isNumberedList = false;

  const flushList = () => {
    if (listBuffer.length > 0) {
      elements.push(
        React.createElement(
          isNumberedList ? 'ol' : 'ul',
          { className: isNumberedList ? "list-decimal pl-5" : "list-disc pl-5" },
          listBuffer.map((item, idx) => (
            <li key={idx} className="mb-1 text-gray-700" dangerouslySetInnerHTML={{ __html: formatTextInline(item) }} />
          ))
        )
      );
      listBuffer = [];
      isNumberedList = false;
    }
  };

  const getIconForHeader = (text) => {
    if (/current state/i.test(text)) return <i className="bi bi-bar-chart-fill text-blue-500 mr-2" style={iconStyle}></i>;
    if (/priority actions/i.test(text)) return <i className="bi bi-check-circle-fill text-green-500 mr-2" style={iconStyle}></i>;
    if (/expected results/i.test(text)) return <i className="bi bi-rocket-fill text-orange-500 mr-2" style={iconStyle}></i>;
    if (/key metrics/i.test(text)) return <i className="bi bi-pin-angle-fill text-purple-500 mr-2" style={iconStyle}></i>;
    return null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (/^### /.test(trimmed)) {
      flushList();
      const text = trimmed.slice(4);
      elements.push(
        <h4 className="text-md font-semibold text-gray-800 mt-4">
          {getIconForHeader(text)}{text}
        </h4>
      );
    } else if (/^## /.test(trimmed)) {
      flushList();
      const text = trimmed.slice(3);
      elements.push(
        <h3 className="text-lg font-bold text-theme_clr1 mt-6">
          {getIconForHeader(text)}{text}
        </h3>
      );
    } else if (/^# /.test(trimmed)) {
      flushList();
      const text = trimmed.slice(2);
      elements.push(
        <h2 className="text-2xl font-bold text-theme_clr1 mt-8">
          {getIconForHeader(text)}{text}
        </h2>
      );
    }
    // Bullet list (*, -, +)
    else if (/^[-*+] /.test(trimmed)) {
      listBuffer.push(trimmed.slice(2));
    }
    // Numbered list (1., 2., ...)
    else if (/^\d+\. /.test(trimmed)) {
      if (!isNumberedList) flushList();
      isNumberedList = true;
      listBuffer.push(trimmed.replace(/^\d+\. /, ""));
    }
    // Markdown table
    else if (
      trimmed.includes("|") &&
      i + 1 < lines.length &&
      /^\s*\|?[-:\s|]+\|?\s*$/.test(lines[i + 1].trim())
    ) {
      flushList();

      const tableLines = [trimmed];
      i += 2; // skip header + separator

      while (i < lines.length) {
        const row = lines[i].trim();

        if (!row.includes("|")) {
          i--;
          break;
        }

        tableLines.push(row);
        i++;
      }

      elements.push(
        <React.Fragment key={`table-${elements.length}`}>
          {parseMarkdownTable(tableLines)}
        </React.Fragment>
      );
    }
    // Paragraphs
    else if (trimmed.length > 0) {
      flushList();
      elements.push(
        <p
          className="text-gray-700 mt-2"
          dangerouslySetInnerHTML={{ __html: formatTextInline(trimmed) }}
        />
      );
    } else {
      flushList();
    }
  }

  flushList();
  return elements;
};

const FormattedAIResponse = ({ rawText }) => {
  return (
    <div key={rawText}>
      {formatLines(rawText)}
    </div>
  );
};

export default FormattedAIResponse;

// USAGE EXAMPLE:
// <FormattedAIResponse rawText={`# Strategy\n## Current State\n- **Point A**\n1. *Step one*`} />